import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Inbox, RefreshCw, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLES = {
  new: "border-amber/40 text-amber",
  contacted: "border-cyan/40 text-cyan",
  closed: "border-white/20 text-white/50",
};

const AdminLeads = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/enquiries")
      .then(({ data }) => setEnquiries(data))
      .catch(() => toast.error("Failed to load enquiries"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const cycleStatus = async (enq) => {
    const order = ["new", "contacted", "closed"];
    const next = order[(order.indexOf(enq.status) + 1) % order.length];
    try {
      await api.patch(`/admin/enquiries/${enq.id}?status=${next}`);
      setEnquiries((list) => list.map((e) => (e.id === enq.id ? { ...e, status: next } : e)));
    } catch {
      toast.error("Could not update status");
    }
  };

  const signOut = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-void" data-testid="admin-enquiries">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-void/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <div>
            <h1 className="font-display text-lg font-extrabold uppercase text-white">Quote Enquiries</h1>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">Shop Front Makers · Admin</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-widest text-white/70 hover:border-amber hover:text-amber">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={signOut} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-widest text-white/70 hover:border-red-400 hover:text-red-400">
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-white/50">
          <Inbox size={16} className="text-amber" />
          {loading ? "Loading…" : `${enquiries.length} total enquiries`}
        </div>

        {!loading && enquiries.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-obsidian p-16 text-center text-white/50" data-testid="admin-empty">
            No enquiries yet. Submissions from the quote form will appear here.
          </div>
        )}

        <div className="space-y-4">
          {enquiries.map((e) => (
            <div key={e.id} data-testid={`enquiry-row-${e.id}`} className="rounded-2xl border border-white/10 bg-obsidian p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-display text-lg font-bold text-white">{e.name}</h3>
                    <button
                      onClick={() => cycleStatus(e)}
                      data-testid={`status-toggle-${e.id}`}
                      className={`rounded-full border px-3 py-0.5 font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLES[e.status]}`}
                    >
                      {e.status}
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-4 text-sm text-white/60">
                    <a href={`mailto:${e.email}`} className="flex items-center gap-1.5 hover:text-amber">
                      <Mail size={13} /> {e.email}
                    </a>
                    {e.phone && (
                      <a href={`tel:${e.phone}`} className="flex items-center gap-1.5 hover:text-amber">
                        <Phone size={13} /> {e.phone}
                      </a>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <span className="rounded-full border border-amber/30 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-amber">
                    {e.service}
                  </span>
                  {e.budget && <p className="mt-2 font-mono text-[11px] text-white/40">Budget: {e.budget}</p>}
                  <p className="mt-1 font-mono text-[10px] text-white/30">{new Date(e.created_at).toLocaleString("en-IE")}</p>
                </div>
              </div>
              <p className="mt-4 border-t border-white/10 pt-4 text-sm leading-relaxed text-white/70">{e.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminLeads;
